// src/pages/Home.tsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FaGlobe } from "react-icons/fa";
import "./Home.css";

const INDUSTRY_LEADERS = [
  { name: "Robby Dinero", img: "/RequestDJImages/robbydinero.jpg", slug: "dj-robby-dinero" },
  { name: "Bang Jackson", img: "/RequestDJImages/bangjackson.jpg", slug: "bang-jackson" },
  { name: "DJ Lucky", img: "/RequestDJImages/djlucky.jpg", slug: "dj-lucky" },
  { name: "DJ Frisco", img: "/RequestDJImages/djfrisco.jpg", slug: "frisco" },
  { name: "DJ Xoul", img: "/RequestDJImages/djxoul.jpg", slug: "dj-xoul" },
  { name: "DJ Donavon", img: "/RequestDJImages/djdonavon.jpg", slug: "donavon" },
  { name: "DJ Benofficial", img: "/RequestDJImages/djbenofficial.jpg", slug: "dj-benofficial" },
  { name: "DJ Lucha Libre", img: "/RequestDJImages/djluchalibre.jpg", slug: "lucha-libre" },
  { name: "DJ Sonny James", img: "/RequestDJImages/djsonnyjames.jpg", slug: "sonny-james" },
] as const;

/* ── Infinite Carousel (JS-powered, works on all devices) ── */
const InfiniteCarousel: React.FC<{ items: readonly { name: string; img: string; slug: string }[] }> = ({ items }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const speedRef = useRef(0.5); // px per frame
  const dragging = useRef(false);
  const startX = useRef(0);
  const dragOffset = useRef(0);
  const lastTouch = useRef(0);
  const velocity = useRef(0);

  // Triple the items so we have plenty to loop
  const tripled = [...items, ...items, ...items];
  const singleWidth = useRef(0);

  // Measure one set of items
  useEffect(() => {
    if (!trackRef.current) return;
    const cards = trackRef.current.children;
    const count = items.length;
    if (cards.length === 0) return;
    let w = 0;
    for (let i = 0; i < count; i++) {
      w += (cards[i] as HTMLElement).offsetWidth + 20; // 20 = gap
    }
    singleWidth.current = w;
    // Start in the middle set
    setOffset(w);
  }, [items.length]);

  // Animation loop
  useEffect(() => {
    let raf: number;
    const animate = () => {
      if (!dragging.current && singleWidth.current > 0) {
        setOffset(prev => {
          let next = prev + speedRef.current + velocity.current;
          // Dampen velocity
          velocity.current *= 0.95;
          if (Math.abs(velocity.current) < 0.1) velocity.current = 0;
          // Loop: if we've scrolled past 2 sets, jump back by 1 set
          if (next >= singleWidth.current * 2) next -= singleWidth.current;
          // Loop: if we've scrolled before 0, jump forward by 1 set
          if (next <= 0) next += singleWidth.current;
          return next;
        });
      }
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Touch handlers
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    dragging.current = true;
    startX.current = e.touches[0].clientX;
    dragOffset.current = offset;
    lastTouch.current = e.touches[0].clientX;
    velocity.current = 0;
  }, [offset]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!dragging.current) return;
    const x = e.touches[0].clientX;
    const diff = lastTouch.current - x;
    lastTouch.current = x;
    velocity.current = diff * 0.3;
    setOffset(prev => {
      let next = prev + diff;
      if (singleWidth.current > 0) {
        if (next >= singleWidth.current * 2) next -= singleWidth.current;
        if (next <= 0) next += singleWidth.current;
      }
      return next;
    });
  }, []);

  const onTouchEnd = useCallback(() => {
    dragging.current = false;
  }, []);

  // Mouse handlers (for desktop dragging)
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    startX.current = e.clientX;
    dragOffset.current = offset;
    lastTouch.current = e.clientX;
    velocity.current = 0;
  }, [offset]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging.current) return;
    const x = e.clientX;
    const diff = lastTouch.current - x;
    lastTouch.current = x;
    velocity.current = diff * 0.3;
    setOffset(prev => {
      let next = prev + diff;
      if (singleWidth.current > 0) {
        if (next >= singleWidth.current * 2) next -= singleWidth.current;
        if (next <= 0) next += singleWidth.current;
      }
      return next;
    });
  }, []);

  const onMouseUp = useCallback(() => {
    dragging.current = false;
  }, []);

  return (
    <div
      className="ambassadors-carousel"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      <div
        ref={trackRef}
        className="ambassadors-track-js"
        style={{ transform: `translateX(${-offset}px)` }}
      >
        {tripled.map((a, i) => (
          <Link key={`${a.name}-${i}`} to={`/${a.slug}`} className="ambassador-card ambassador-card--link">
            <div className="ambassador-img-wrap">
              <img src={a.img} alt={a.name} className="ambassador-img ambassador-img--talent" loading="lazy" draggable={false} />
            </div>
            <span className="ambassador-name">{a.name}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

const Home: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const dots = Array.from({ length: 90 }, () => ({
      angle: Math.random() * Math.PI * 2,
      radius: 80 + Math.random() * 340,
      speed: (0.00015 + Math.random() * 0.00025) * (Math.random() > 0.5 ? 1 : -1),
      size: 0.8 + Math.random() * 1.4,
      opacity: 0.1 + Math.random() * 0.22,
      color: Math.random() > 0.55 ? "#22c55e" : "#ffffff",
      yFlatten: 0.3 + Math.random() * 0.25,
    }));

    let raf: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      dots.forEach((dot) => {
        dot.angle += dot.speed;
        const x = cx + Math.cos(dot.angle) * dot.radius;
        const y = cy + Math.sin(dot.angle) * dot.radius * dot.yFlatten;
        ctx.beginPath();
        ctx.arc(x, y, dot.size, 0, Math.PI * 2);
        ctx.fillStyle = dot.color;
        ctx.globalAlpha = dot.opacity;
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div className="home">

      {/* ── Hero ── */}
      <section className="hero">
        <canvas ref={canvasRef} className="hero-canvas" aria-hidden />
        <h1 className="hero-title">
          <span className="hero-shimmer">REQUEST APP</span>
        </h1>
        <p className="hero-tagline">Turn Influence to Income</p>
        <div className="hero-cta">
          <a href="/register" className="btn btn-green-glow btn-lg">
            Join as talent
          </a>
          <Link to="/learn-more" className="btn btn-outline-white btn-lg">
            Learn More
          </Link>
        </div>
      </section>

      {/* ── 3 Stats Bar ── */}
      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-check">✓</span>
          <span className="stat-value">15K+</span>
          <span className="stat-label">Active Performers</span>
        </div>
        <div className="stat-divider" />
        <div className="stat-item">
          <span className="stat-check">✓</span>
          <span className="stat-value">$1M+</span>
          <span className="stat-label">Paid to Creators</span>
        </div>
        <div className="stat-divider" />
        <div className="stat-item">
          <span className="stat-check">✓</span>
          <span className="stat-value">100K+</span>
          <span className="stat-label">Requests Fulfilled</span>
        </div>
      </div>

      {/* ── Your Link ── */}
      <section className="section section-dark storefront-showcase">
        <h2 className="section-heading storefront-showcase-heading">
          Your Link. <span className="heading-accent">Your Storefront.</span>
        </h2>
        <div className="storefront-preview">
          <span className="storefront-url-prefix">REQUEST.APP/</span>
          <span className="storefront-url-name">NAME</span>
        </div>
        <p className="storefront-sub">Requests · Promos · Features · Bookings · Playlists & More</p>
      </section>

      {/* ── Trusted by Industry Leaders ── */}
      <section className="section section-gradient ambassadors-section">
        <h2 className="section-heading">
          Trusted by <span className="heading-accent">Industry Leaders</span>
        </h2>
        <InfiniteCarousel items={INDUSTRY_LEADERS} />
      </section>

      {/* ── Borderless by Design ── */}
      <section className="section section-gradient borderless-section">
        <h2 className="section-heading">
          <span className="heading-accent">Borderless</span> by Design
        </h2>
        <FaGlobe className="borderless-globe" aria-hidden />
        <p className="borderless-sub">One Profile. Any Setup. Any Country.</p>
        <p className="borderless-tagline">Borderless. Transparent. Secure.</p>
      </section>

      {/* ── CTA ── */}
      <section className="section section-dark section-cta">
        <h2 className="section-heading section-heading-lg">
          Join thousands of DJs,<br />
          <span className="heading-accent">Artists & Performers.</span>
        </h2>
        <p className="section-text">
          Monetize your influence — on your terms, in any country.
        </p>
        <div className="cta-buttons">
          <a href="/register" className="btn btn-green-glow btn-lg">
            Start Earning Free
          </a>
          <a href="/login" className="btn btn-outline-white btn-lg">
            Login
          </a>
        </div>
      </section>

    </div>
  );
};

export default Home;
